import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, EyeOff, FileCode2, Fingerprint, Lock, Shuffle, Sparkle, Waves } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { LiveActivity } from "./LiveActivity";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { name: "PNG", detail: "caBX / eXIf / tEXt / iTXt" },
  { name: "JPEG", detail: "APP1–APP14, APP11 JUMBF, COM" },
  { name: "SVG", detail: "<metadata>, RDF, XMP" },
  { name: "PDF", detail: "XMP packets, /Producer, /Creator" },
  { name: "DOCX", detail: "docProps, customXml" },
  { name: "ODT", detail: "meta.xml generator" },
  { name: "HTML", detail: "meta, JSON-LD, data-ai*" },
  { name: "Markdown", detail: "YAML frontmatter keys" },
  { name: "TXT / CSV / JSON", detail: "Layer A Unicode scrub" },
  { name: "MP4 / MOV", detail: "udta / meta / uuid XMP boxes" },
];

const Feature = ({ icon: Icon, title, body, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0d0d0f] p-7 hover:border-sky-500/30 hover:bg-[#101013] transition-colors duration-300 ${className}`}
  >
    <Icon className="h-6 w-6 text-sky-400 mb-5" strokeWidth={1.5} />
    <h3 className="font-head text-xl font-medium mb-2.5">{title}</h3>
    <p className="text-sm leading-relaxed text-slate-400">{body}</p>
  </div>
);

export default function Landing() {
  const { t } = useI18n();
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setCount(r.data.items_cleaned)).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-[#050505]">
      {/* Hero */}
      <section className="relative overflow-hidden grain border-b border-white/5">
        <img
          src="https://images.pexels.com/photos/15680091/pexels-photo-15680091.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-sky-500/20 blur-[130px]" />

        <div className="relative mx-auto max-w-6xl px-5 pt-24 pb-28 sm:pt-32 sm:pb-36">
          <div className="rise inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
            <Sparkle className="h-3 w-3 text-sky-400" strokeWidth={2} />
            {t("hero.badge")}
          </div>

          <h1 className="rise mt-8 max-w-4xl font-head text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            {t("hero.title1")}
            <br />
            <span className="text-sky-400">{t("hero.title2")}</span>
          </h1>

          <p className="rise mt-7 max-w-2xl text-base leading-relaxed text-slate-400" style={{ animationDelay: "160ms" }}>
            {t("hero.sub")}
          </p>

          <div className="rise mt-10 flex flex-wrap items-center gap-3" style={{ animationDelay: "240ms" }}>
            <Link
              to="/studio"
              data-testid="hero-cta-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-500 px-6 py-3 font-medium text-white shadow-[0_0_28px_rgba(14,165,233,0.35)] transition-colors hover:bg-sky-400 active:scale-[0.98]"
            >
              {t("hero.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2} />
            </Link>
            <a
              href="#formats"
              data-testid="hero-secondary-btn"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
            >
              {t("hero.cta2")}
            </a>
          </div>

          <dl className="rise mt-20 grid max-w-3xl grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.06] sm:grid-cols-3" style={{ animationDelay: "320ms" }}>
            {[
              ["hero.stat1", "hero.stat1v"],
              ["hero.stat2", "hero.stat2v"],
              ["hero.stat3", "hero.stat3v"],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#08080a] px-6 py-5">
                <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t(k)}</dt>
                <dd className="mt-2 font-head text-lg font-medium text-white">{t(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Live activity */}
      <LiveActivity realCount={count || 0} />

      {/* Features */}
      <section className="mx-auto max-w-6xl px-5 py-24 sm:py-32">
        <div className="grid gap-4 md:grid-cols-3">
          <Feature testid="feature-layer-a" icon={EyeOff} title={t("feat.a.title")} body={t("feat.a.body")} className="md:col-span-2" />
          <Feature testid="feature-privacy" icon={Lock} title={t("feat.d.title")} body={t("feat.d.body")} />
          <Feature testid="feature-c2pa" icon={Fingerprint} title={t("feat.b.title")} body={t("feat.b.body")} />
          <Feature testid="feature-exif" icon={FileCode2} title={t("feat.c.title")} body={t("feat.c.body")} className="md:col-span-2" />
          <Feature testid="feature-statistical" icon={Shuffle} title={t("feat.e.title")} body={t("feat.e.body")} className="md:col-span-2" />
          <Feature testid="feature-synthid" icon={Waves} title={t("feat.f.title")} body={t("feat.f.body")} />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-white/5 bg-[#08080a]">
        <div className="mx-auto max-w-6xl px-5 py-24 sm:py-32">
          <h2 className="font-head text-3xl font-semibold tracking-tight sm:text-4xl">{t("how.title")}</h2>
          <ol className="mt-14 grid gap-10 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <li key={n} data-testid={`how-step-${n}`} className="border-t border-white/10 pt-6">
                <span className="font-mono2 text-xs text-sky-400">0{n}</span>
                <h3 className="mt-3 font-head text-xl font-medium">{t(`how.${n}t`)}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{t(`how.${n}b`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Formats */}
      <section id="formats" className="mx-auto max-w-6xl px-5 py-24 sm:py-32">
        <h2 className="font-head text-3xl font-semibold tracking-tight sm:text-4xl">{t("formats.title")}</h2>
        <p className="mt-4 max-w-xl text-base text-slate-400">{t("formats.sub")}</p>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-3">
          {FORMATS.map((f) => (
            <div key={f.name} data-testid={`format-${f.name.split(" ")[0].toLowerCase()}`} className="bg-[#0b0b0d] px-6 py-5 transition-colors hover:bg-[#111114]">
              <p className="font-head text-base font-medium text-white">{f.name}</p>
              <p className="mt-1 font-mono2 text-[11px] leading-relaxed text-slate-500">{f.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/5 bg-[#08080a]">
        <div className="mx-auto max-w-3xl px-5 py-24 sm:py-32">
          <h2 className="font-head text-3xl font-semibold tracking-tight sm:text-4xl">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {[1, 2, 3, 4, 5].map((n) => (
              <AccordionItem key={n} value={`q${n}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${n}`} className="text-left font-head text-base font-medium hover:no-underline hover:text-sky-400">
                  {t(`faq.q${n}`)}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">{t(`faq.a${n}`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

    </main>
  );
}
